/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'

import {
  Box,
  Divider,
  Flex,
  Spinner
} from '@chakra-ui/react'
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi'
import Header from './components/Header'
import CommonData from './components/CommonData'
import Buttons from './components/Buttons'
import Message from './components/Message'

const variants = {
  success: {
    colorScheme: 'green',
    title: 'Registration complete',
    icon: FiCheckCircle
  },
  warning: {
    colorScheme: 'orange',
    title: 'Registration completed with warnings',
    icon: FiAlertTriangle
  },
  error: {
    colorScheme: 'red',
    title: 'Registration failed',
    icon: FiXCircle
  },
  processing: {
    colorScheme: 'blue',
    title: 'Registration in progress',
    icon: null
  }
}

export default function ProcessNotification ({
  closeLabel,
  currentCount,
  description,
  errorMessage,
  estimatedTime,
  isParallelProcess,
  itemIcon,
  itemLabel,
  itemName,
  itemUrl,
  onClose,
  processed,
  processedLabel,
  status = 'success',
  successfulCount,
  title,
  totalItems,
  viewItemLabel,
  warningMessage
}) {
  const variant = variants[status]
  const { colorScheme, icon: StatusIcon } = variant
  const isProcessing = status === 'processing'
  const isDone = status !== 'processing'

  return (
    <Box
      role="alert"
      border="1px solid"
      borderColor={`${colorScheme}.200`}
      bg={`${colorScheme}.50`}
      borderRadius="base"
      p={5}
    >
      <Flex
        align={{ base: 'flex-start' }}
        wrap={{ base: 'wrap', md: 'nowrap' }}
        gap={4}
      >
        <Flex
          boxSize="12"
          borderRadius="full"
          bg={StatusIcon ? `${colorScheme}.100` : 'transparent'}
          align="center"
          justify="center"
          flexShrink={0}
        >
          { StatusIcon
            ? <Box
                as={StatusIcon}
                boxSize={6}
                color={`${colorScheme}.600`}
              />
            : <Spinner
                boxSize={8}
                thickness="3px"
                speed="0.65s"
                color={`${colorScheme}.500`}
                emptyColor={`${colorScheme}.100`}
              />
          }
        </Flex>

        <Box flex="1" minW="0">
          <Header
            title={title || variant.title}
            description={description}
            colorScheme={colorScheme}
            onClose={onClose}
          />
          <Divider my={3} borderColor={`${colorScheme}.200`} />
          <CommonData
            colorScheme={colorScheme}
            currentCount={currentCount}
            estimatedTime={estimatedTime}
            isDone={isDone}
            isParallelProcess={isParallelProcess}
            isProcessing={isProcessing}
            itemIcon={itemIcon}
            itemLabel={itemLabel}
            itemName={itemName}
            processed={processed}
            processedLabel={processedLabel}
            status={status}
            successfulCount={successfulCount}
            totalItems={totalItems}
          />

          { status === 'warning' && warningMessage &&
            <Message status="warning" message={warningMessage} />
          }
          { status === 'error' && errorMessage &&
            <Message status="error" message={errorMessage} />
          }
          { isProcessing &&
            <Message status="info" message="This process may take several minutes." />
          }
          <Divider my={3} borderColor={`${colorScheme}.200`} />
          {(itemUrl || onClose) && !isProcessing &&
            <Box flexShrink={0} alignSelf={{ base: 'flex-start', md: 'center' }}>
              <Buttons
                colorScheme={colorScheme}
                itemUrl={itemUrl}
                onClose={onClose}
                secondaryLabel={closeLabel}
                viewItemLabel={viewItemLabel}
              />
            </Box>
          }
        </Box>

      </Flex>
    </Box>
  )
}
